import Search from "../components/form/Search";
import Container from "../components/shared/Container";
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "../components/form/Button";
import Dropdown from "../components/form/Dropdown";
import Image from "../components/elements/Image";
import { useContext, useEffect, useState } from "react";
import { classNames } from "../helpers/classNames";
import { AnimatePresence, motion } from "motion/react";
import { lngDetect } from "../helpers/lngDetect";
import { changeOrganization } from "../services/organizationService";
import { useDispatch } from "react-redux";
import { resetOrganization, setOrganization } from "../redux/slice/organizationSlice";
import { useMutation } from "@tanstack/react-query";
import useFindRoute from "../hooks/useFindRoute";
import { useTranslation } from "react-i18next";
import coinImg from "../assets/images/coin.svg";
import SocketContext, { useSocket } from "../context/SocketContext";
import EventAddModal from "../components/common/EventAddModal";
import Select from "../components/form/select/Select";
import { langConfig } from "../config/LangConfig";
import { setLanguage } from "../redux/slice/languageSlice";

const PRESERVED_KEYS = [
  "savedEmail",
  "savedPassword",
  "rememberMe",
  "theme", // if you have theme preference
  "lang", // if you have language preference
];

const getPreservedData = () => {
  const preserved = {};
  PRESERVED_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value) {
      preserved[key] = value;
    }
  });
  return preserved;
};

const restorePreservedData = (preservedData) => {
  Object.keys(preservedData).forEach((key) => {
    localStorage.setItem(key, preservedData[key]);
  });
};

const FrontHeader = ({ profile, organizer }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const params = useParams();
  const { socket } = useContext(SocketContext);
  const { matchedRoute, replaceParams } = useFindRoute();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useSelector((state) => state?.authProvider);
  const { lang } = useSelector((state) => state?.langProvider);
  const [isSideMenu, setSideMenu] = useState(false);
  const [messages, setMessages] = useState({ count: 0 });
  const { appliedTheme, selectedElement, status } = useSelector((state) => state?.themeProvider);
  const { selectedOrganization, organizations, selectedOrganizationDetails } = useSelector((state) => state?.organization);

  useEffect(() => {
    if (socket?.connected && session?.user?._id) {
      socket?.emit("messageUnreadCountEvent", { user: session?.user?._id });
    }
  }, [session?.user?._id]);

  useSocket(
    "messageUnreadCountEventResponse",
    (res) => {
      if (session?.user?._id === res?.user && res?.countFor !== "event") {
        setMessages((pre) => ({ ...pre, count: res?.data }));
      }
    },
    [session?.user?._id]
  );

  useSocket(
    "getMessageDetailEventResponse",
    (res) => {
      socket?.emit("messageUnreadCountEvent", { user: session?.user?._id });
    },
    [session?.user?._id]
  );

  const dropdownItems = [
    {
      _id: "messages",
      name: (
        <div className="flex items-center justify-between">
          {t("Messages")}{" "}
          {messages?.count > 0 && (
            <span className="flex items-center justify-center w-6 h-6 text-xs p-1 rounded-full text-white bg-primary">
              {messages?.count > 99 ? "99+" : messages?.count}
            </span>
          )}
        </div>
      ),
      icon: "fa-regular fa-message",
      link: `/${lang}/user/communication`,
      class: "text-sm px-3 py-2 rounded transition-all duration-200",
    },
    {
      _id: "tickets",
      name: t("Tickets"),
      icon: "fa-regular fa-clipboard-list",
      link: `/${lang}/tickets-order`,
      class: "text-sm px-3 py-2 rounded transition-all duration-200",
    },
    {
      _id: "following",
      name: t("Following"),
      icon: "fas fa-users",
      link: `/${lang}/following`,
      class: "text-sm px-3 py-2 rounded transition-all duration-200",
    },
    {
      _id: "promotion_events",
      name: t("Promotion events"),
      icon: "fas fa-calendar-days",
      link: `/${lang}/promotion-events`,
      class: "text-sm px-3 py-2 rounded transition-all duration-200",
    },
    {
      _id: "earnings",
      name: (
        <div className="flex items-center justify-between flex-1">
          {t("Earnings")}{" "}
          {/* <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <Image src={coinImg} alt={"coin"} />
            </div>
            0
          </div> */}
        </div>
      ),
      icon: "fas fa-sack-dollar",
      link: `/${lang}/earnings`,
      class: "text-sm px-3 py-2 rounded transition-all duration-200",
    },
    {
      _id: "profile",
      name: t("Profile"),
      icon: "fa-regular fa-user-cog",
      link: `/${lang}/profile`,
      class: "text-sm px-3 py-2 rounded transition-all duration-200",
    },
    {
      _id: "account_settings",
      name: t("Account settings"),
      icon: "fa-regular fa-user-cog",
      link: `/${lang}/user/account/account-setting`,
      class: "text-sm px-3 py-2 rounded transition-all duration-200",
    },
    ...(session?.user?.role === "organization"
      ? [
          {
            _id: "divider",
            type: "divider",
          },
          ...(organizations?.data?.data || [])?.map((item) => ({
            _id: item?.selectedOrganization?._id,
            name: lngDetect(item?.organizationData?.name), // dynamic, leave as is
            link: "",
            class:
              item?.organizationData?._id === selectedOrganization
                ? "bg-primary/10 text-sm text-deep-purple px-3 py-2 rounded transition-all duration-200 font-medium"
                : "text-deep-purple px-3 py-2 rounded transition-all duration-200",
            click: () => {
              changeOrganizationHandler(item);
            },
          })),
        ]
      : []),
    ...(localStorage.getItem("original_admin_access_token")
      ? [
          {
            _id: "go back",
            name: t("Go Back as Admin"),
            icon: "fa-solid fa-user-shield",
            click: () => {
              const adminToken = localStorage.getItem("original_admin_access_token");
              const adminRefreshToken = localStorage.getItem("original_admin_refresh_token");

              if (adminToken && adminRefreshToken) {
                localStorage.setItem("auth_token", adminToken);
                localStorage.setItem("refreshToken", adminRefreshToken);
                localStorage.removeItem("original_admin_access_token");
                localStorage.removeItem("original_admin_refresh_token");

                window.location.reload();
                window.location.href = `/${lang}/admin/users`
              }
            },
            class: "text-sm px-3 py-2 rounded font-medium transition-all duration-200",
            standout: true,
          },
        ]
      : []),
    {
      _id: "logout",
      name: t("Log out"),
      icon: "fa-regular fa-arrow-right-from-bracket",
      click: () => {
        dispatch(resetOrganization());
        const preservedData = getPreservedData();
        localStorage.clear();
        sessionStorage.clear();
        restorePreservedData(preservedData);
        window.location.href = "/";
      },
      class: "text-sm px-3 py-2 rounded font-medium transition-all duration-200",
      standout: true,
    },
  ];

  const { mutate: changeOrganizationHandler, isPending: organizationChangeInPending } = useMutation({
    mutationFn: async (item) => {
      return await changeOrganization({
        organization: item?.organizationData?._id,
      });
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        sessionStorage.setItem("organization", res?.data?.organizationData?._id);
        localStorage.setItem("organization", res?.data?.organizationData?._id);
        dispatch(setOrganization(res?.data?.organizationData?._id));
        if (matchedRoute?.split("/")?.[2] === "user") {
          const path = replaceParams(matchedRoute, {
            lang,
            orgId: res?.data?.organizationData?.uid,
          });
          navigate(path);
        }
      }
    },
  });

  useEffect(() => {
    const html = document.querySelector("html");
    if (isSideMenu) {
      html.style.overflow = "hidden";
      html.style.paddingRight = "17px";
    } else {
      html.style.overflow = "";
      html.style.paddingRight = "";
    }
  }, [isSideMenu]);

  useEffect(() => {
    setSideMenu(false);
  }, [location]);

  const sidemenuData = [
    { name: t("Events"), link: `/${lang}/events` },
    { name: t("About us"), link: "" },
    { name: t("Pricing"), link: "" },
    { name: t("Contact"), link: "" },
    { name: t("Media"), link: "" },
    { name: t("FAQs"), link: "" },
  ];

  const sidemenuOtherData = [
    { name: t("Event Marketing Platform"), link: "" },
    { name: t("Event Registration Software"), link: "" },
    { name: t("Community Guidelines"), link: "" },
    { name: t("Event Payment System"), link: "" },
    { name: t("Event Management"), link: "" },
    { name: t("QR Codes for Event Check-In"), link: "" },
    { name: t("Post your event online"), link: "" },
  ];

  const socialData = [
    { name: "Instagram", link: "", icon: "fa-brands fa-fw fa-instagram" },
    { name: "Tiktok", link: "", icon: "fa-brands fa-fw fa-tiktok" },
    { name: "Twitter-X", link: "", icon: "fa-brands fa-fw fa-x-twitter" },
    { name: "Facebook", link: "", icon: "fa-brands fa-fw fa-facebook-f" },
  ];

  function languageChangHandler(val) {
    const accepted_lng = langConfig;
    const lng = localStorage.getItem("lang");
    if (val && !accepted_lng?.includes(val)) {
      localStorage.setItem("lang", "en");
      const path = replaceParams(matchedRoute, {
        ...params,
        lang: "en",
      });
      navigate(path);
    } else {
      localStorage.setItem("lang", val);
      const path = replaceParams(matchedRoute, {
        ...params,
        lang: val,
      });
      navigate(path, { state: location?.state || {} });
      i18n.changeLanguage(val);
      dispatch(setLanguage(val));
    }
    if (!accepted_lng?.includes(lng)) {
      localStorage.setItem("lang", "en");
    }
  }

  return (
    <>
      <header className="header bg-surface-04 w-full sticky top-0 z-50">
        <Container className={"justify-between h-24 items-center max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl gap-6 px-6"}>
          <Link className="h-12 max-w-36 xl:max-w-40 2xl:max-w-48" to={"/"} replace>
            <Image
              src={appliedTheme?.logo instanceof File ? URL.createObjectURL(appliedTheme?.logo) : appliedTheme?.logo?.url || appliedTheme?.logo}
              alt={"Logo"}
              classes={"object-contain"}
            />
          </Link>
          {/* <div className="w-80 2xl:w-[410px] mx-auto">
            <Search divClasses="bg-transparent" />
          </div> */}
          <div className="flex items-center gap-3">
            <div className="relative hidden md:block">
              <Button
                buttonIconPosition="left"
                buttonIcon={"fa-solid fa-heart"}
                buttonClasses={"text-gradient-red bg-transparent px-0"}
                buttonFunction={() => {
                  navigate(`/${lang}/wishlist`);
                }}
              />
            </div>
            {session?.status === "unauthorized" && session?.status !== "loading" && (
              <div className="relative hidden md:block">
                <Button
                  buttonIconPosition="left"
                  buttonIcon={"fa-solid fa-user"}
                  buttonClasses={"bg-transparent px-0 text-deep-purple"}
                  buttonFunction={() => navigate("/login")}
                />
              </div>
            )}
            {session?.user?.role === "organization" && (
              <div className="relative hidden md:block">
                <EventAddModal triggerButtonLabel={"Publish event"} triggerBtnIcon={"fa-regular fa-add"} />
              </div>
            )}
            {session?.status === "authorized" && session?.status !== "loading" && session?.user?._id && (
              <div className="relative">
                <Dropdown
                  data={dropdownItems}
                  buttonLabel={
                    <div className="text-start space-y-1">
                      {session?.user?.role === "organization" && (
                        <div className="flex items-center gap-3">
                          {lngDetect(selectedOrganizationDetails?.data?.name) && (
                            <div className="truncate max-w-36">{lngDetect(selectedOrganizationDetails?.data?.name)}</div>
                          )}
                          {messages?.count > 0 && (
                            <span className="ml-auto flex items-center justify-center w-5 h-5 text-[10px] p-1 rounded-full text-white bg-primary">
                              {messages?.count > 99 ? "99+" : messages?.count}
                            </span>
                          )}
                        </div>
                      )}
                      <div className={session?.user?.role === "organization" ? "!text-[10px]" : "flex justify-between items-center"}>
                        <div className="line-clamp-1 max-w-32" style={{ wordBreak: "break-word" }}>
                          {session?.user?.lastName ? `${session?.user?.firstName} ${session?.user?.lastName}` : session?.user?.firstName}
                        </div>

                        {session?.user?.role === "user" && messages?.count > 0 && (
                          <span className="ml-1 flex items-center justify-center w-5 h-5 text-[10px] p-1 rounded-full text-white bg-primary">
                            {messages?.count > 99 ? "99+" : messages?.count}
                          </span>
                        )}
                      </div>
                    </div>
                  }
                  xPlacement="bottomRight"
                  buttonIcon="fa-regular fa-user"
                  buttonIconPosition="left"
                  dropdownContainer="
                      w-64 md:w-72 rounded-lg bg-white border border-gray-200 shadow-lg ring-1 ring-black/5 
                      px-2 py-3 z-50 transition-all duration-200
                      before:content-[''] before:absolute before:-top-1.5 before:right-6 
                      before:w-3 before:h-3 before:rotate-45 
                      before:bg-white before:border-l before:border-t before:border-gray-200
                    "
                  buttonClasses="h-12 rounded-full border-none bg-white px-4 pr-auto md:pr-10 shadow-sm hover:shadow-md transition-all"
                  buttonLabelClasses="text-sm text-deep-purple font-semibold uppercase hidden lg:inline-block"
                  buttonArrowIcon="fa-regular fa-chevron-down"
                  buttonArrowIconClasses="text-xs text-gray-400 hidden md:block"
                  manage={true}
                  manageLabel={t("Organization")}
                  manageLinkLabel={t("Manage")}
                  manageLink={`/${lang}/user/organization/overview`}
                  manageIcon="fa-regular fa-sliders"
                  dropdownHeader={false}
                  dropdownHeaderLabel={"Organization link"}
                  dropdownHeaderLink={"https://buytickets.at/devteam"}
                />
              </div>
            )}
            <div className="w-12 max-w-12">
              <Select
                selectedValue={lang}
                dropdownButtonClass={"bg-white"}
                dropdownClass={"w-32"}
                dropdownData={[
                  {
                    name: "En",
                    value: "en",
                    image: require("../assets/images/en.png"),
                  },
                  {
                    name: "Es",
                    value: "es",
                    image: require("../assets/images/es.png"),
                  },
                ]}
                onChange={languageChangHandler}
              />
            </div>
            <Button
              buttonClasses={"!bg-none !p-0 !h-10 !w-10 flex-shrink-0"}
              buttonLabel={
                <div className="flex flex-col gap-1.5 items-end mx-auto">
                  <span className="w-6 h-[3px] bg-slate-900 rounded-full"></span>
                  <span className="w-8 h-[3px] bg-slate-900 rounded-full"></span>
                  <span className="w-4 h-[3px] bg-slate-900 rounded-full"></span>
                </div>
              }
              buttonFunction={() => setSideMenu(!isSideMenu)}
            />
          </div>
        </Container>
      </header>
      <AnimatePresence mode="wait">
        {isSideMenu && (
          <>
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed inset-0 z-80 bg-black/50 cursor-pointer"
              onClick={() => setSideMenu(false)}
            />

            <motion.aside
              key="sidebar"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 right-0 w-full max-w-lg h-full z-[100] bg-white flex shadow-[-10px_0_60px_rgba(0,0,0,0.25)] rounded-l-3xl"
            >
              <div className="flex-shrink flex-grow w-full">
                <div className="h-20 flex justify-between px-4 items-center">
                  <div className="flex-shrink-0 ml-auto">
                    <Button
                      buttonIcon={"fa-light fa-xmark text-3xl"}
                      buttonIconPosition="left"
                      buttonClasses={"!px-0 aspect-square hover:!bg-black/10"}
                      buttonFunction={() => setSideMenu(false)}
                    />
                  </div>
                </div>
                <div className="relative h-[calc(100vh-6rem)] overflow-auto scrollbar px-8 flex">
                  <div className="flex-shrink flex-grow flex flex-col w-full">
                    <div className="flex items-center gap-3 md:hidden">
                      <div className="relative">
                        <Button
                          buttonIconPosition="left"
                          buttonIcon={"fa-solid fa-heart"}
                          buttonClasses={"text-gradient-red bg-transparent px-0"}
                          buttonFunction={() => {
                            navigate(`/${lang}/wishlist`);
                          }}
                        />
                      </div>
                      {session?.status === "unauthorized" && session?.status !== "loading" && (
                        <div className="relative">
                          <Button
                            buttonIconPosition="left"
                            buttonIcon={"fa-solid fa-user"}
                            buttonClasses={"bg-transparent px-0 text-deep-purple"}
                            buttonFunction={() => navigate("/login")}
                          />
                        </div>
                      )}
                      <div className="relative">
                        <Button
                          buttonIcon={"fa-regular fa-add"}
                          buttonLabel={t("Publish event")}
                          buttonLabelClasses={"uppercase"}
                          buttonFunction={() => {
                            navigate(`/${lang}/user/events/event-manage`);
                          }}
                        />
                      </div>
                    </div>

                    {sidemenuData?.length > 0 && (
                      <div className="space-y-2 py-8">
                        {sidemenuData?.map((item, index) => (
                          <div key={index} className="flex">
                            <Link
                              to={item?.link}
                              className="text-deep-purple hover:text-white text-3xl font-bold relative after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-full after:h-0.5 after:bg-white after:pointer-events-none after:origin-[100%_50%] after:scale-x-0 after:scale-y-100 after:scale-z-100 after:transition-transform after:duration-300 after:ease-in-out hover:after:origin-[0%_50%] hover:after:scale-x-100 py-2 hover:bg-primary w-full px-4 rounded-md transition-all duration-300"
                            >
                              {item?.name}
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}

                    {sidemenuOtherData?.length > 0 && (
                      <div className="space-y-4 py-8 border-t border-white/20">
                        <h2 className="text-lg font-bold text-deep-purple underline underline-offset-8 decoration-2">Links</h2>
                        <div className="space-y-2">
                          {sidemenuOtherData?.map((item, index) => (
                            <div key={index} className="flex">
                              <Link to={item?.link} className="text-deep-purple text-base transition-all duration-300 hover:translate-x-4">
                                {item?.name}
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {socialData?.length > 0 && (
                      <div className="space-y-4 py-8 border-t border-white/20">
                        <div className="flex items-center justify-center gap-4">
                          {socialData?.map((item, index) => (
                            <div key={index} className="flex">
                              <a
                                href={item?.link}
                                target="_blank"
                                rel="noreferrer"
                                className={classNames(
                                  "text-deep-purple text-2xl flex items-center justify-center w-12 aspect-square rounded-full relative z-0 overflow-hidden",
                                  "before:content-[''] before:absolute before:inset-0 before:rounded-full before:z-[-1] before:transition-all before:duration-300 before:bg-white/25 before:scale-0 hover:before:scale-100",
                                  "after:content-[''] after:absolute after:inset-0 after:rounded-full after:z-[-1] after:transition-all after:duration-300 after:border after:border-white/25 after:scale-100 hover:after:scale-0"
                                )}
                                title={item?.name}
                              >
                                <i className={item?.icon}></i>
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FrontHeader;
